import React, { useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import './TeacherJoinModal.css';

/**
 * TeacherJoinModal Component
 *
 * Handles teacher registration/login when joining a meeting via student link.
 * Teachers register with Google OAuth and get a persistent account (not guest).
 *
 * Flow:
 * 1. Teacher clicks student's meeting link
 * 2. Modal shows "Sign in with Google" button
 * 3. Teacher signs in with Google
 * 4. If new: Creates teacher account with 7-day trial + role='teacher'
 * 5. If existing: Signs in and joins meeting
 * 6. Teacher gets persistent account with access to all features
 */
const TeacherJoinModal = ({ onJoin, onError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) {
      const errorMsg = 'No credential received from Google';
      setError(errorMsg);
      onError(errorMsg);
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Get browser fingerprint for trial abuse prevention
      const browserFingerprint = await getBrowserFingerprint();

      // Call backend to register/login teacher with Google
      await onJoin({
        googleIdToken: credentialResponse.credential,
        userRole: 'teacher', // Always register as teacher when joining from meeting link
        browserFingerprint,
        redirectTo: window.location.pathname, // Remember meeting link
      });
    } catch (err) {
      console.error('Failed to sign in with Google:', err);
      const errorMsg = err.response?.data?.error || 'Failed to sign in. Please try again.';
      setError(errorMsg);
      onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    const errorMsg = 'Google sign-in failed. Please try again.';
    setError(errorMsg);
    onError(errorMsg);
    setLoading(false);
  };

  /**
   * Generate browser fingerprint for trial abuse prevention
   * Combines multiple browser/device characteristics
   */
  const getBrowserFingerprint = async () => {
    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.hardwareConcurrency || '',
      navigator.deviceMemory || '',
      window.screen.width + 'x' + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
    ];

    const fingerprint = components.join('|');

    // Simple hash function (FNV-1a)
    let hash = 2166136261;
    for (let i = 0; i < fingerprint.length; i++) {
      hash ^= fingerprint.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    return (hash >>> 0).toString(36);
  };

  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'your-google-client-id') {
    return (
      <div className="teacher-portal-overlay">
        <div className="teacher-portal-container">
          <div className="teacher-portal-header">
            <h1>Configuration Required</h1>
          </div>
          <div className="teacher-portal-content">
            <div className="portal-error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment variables.
            </div>
            <p className="portal-terms">
              This is a development environment. In production, Google Sign-In will be enabled.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="teacher-portal-overlay">
        <div className="teacher-portal-container">
          {/* Header with branding */}
          <div className="teacher-portal-header">
            <div className="brand-logo">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                <path d="M12 6v6M9 9l3 3 3-3" stroke="var(--primary-400)" strokeWidth="2.5" />
              </svg>
            </div>
            <h1>Welcome, Instructor!</h1>
            <p className="header-subtitle">
              You've been invited to join a language practice session
            </p>
          </div>

          {/* Main content */}
          <div className="teacher-portal-content">
            {error && (
              <div className="portal-error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            {/* What happens next section */}
            <div className="portal-info-card">
              <h3>What happens next?</h3>
              <div className="portal-steps">
                <div className="portal-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Sign in with Google</h4>
                    <p>We'll create your instructor account instantly</p>
                  </div>
                </div>
                <div className="portal-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Join the meeting</h4>
                    <p>You'll be placed directly into the practice session</p>
                  </div>
                </div>
                <div className="portal-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Start teaching</h4>
                    <p>Help your student practice their language skills</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Sign-in button */}
            <div className="portal-signin-section">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="filled_black"
                size="large"
                text="continue_with"
                shape="rectangular"
                logo_alignment="left"
                width="100%"
              />
            </div>

            {/* Benefits */}
            <div className="portal-benefits">
              <div className="benefit-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Free forever for instructors</span>
              </div>
              <div className="benefit-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Join unlimited practice sessions</span>
              </div>
              <div className="benefit-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>No credit card required</span>
              </div>
            </div>

            {/* Terms */}
            <p className="portal-terms">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>

          {/* Loading overlay */}
          {loading && (
            <div className="portal-loading-overlay">
              <div className="spinner-large"></div>
              <p className="loading-text">Creating your account...</p>
            </div>
          )}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default TeacherJoinModal;
